<template>
  <div class="sf-question-date">
    <div class="sf-question-date__box">
      <div class="sf-question-date__picker-wrap">
        <label :for="pickerId" class="sf-question-date__label">
          Kies je aankomstdag:
        </label>
        <date-picker
          :id="pickerId"
          v-model="pickedDate"
          type="date"
          :placeholder="placeholderText"
          :disabled-date="disabledDates"
          :editable="false"
          :clearable="true"
          format="DD-MM-YYYY"
          value-type="date"
        ></date-picker>
      </div>

      <div v-if="calculatedRange" class="sf-question-date__preview">
        <span class="sf-question-date__preview-icon" aria-hidden="true">📅</span>
        <span class="sf-question-date__preview-text">
          Gekozen periode: <strong>{{ calculatedRange.label }}</strong>
        </span>
      </div>

      <div class="sf-question-date__actions">
        <button
          v-if="question.show_random_button"
          type="button"
          class="sf-question-date__btn-alt"
          @click="chooseForMe"
        >
          Maakt niet uit
        </button>
        <button
          type="button"
          class="sf-question-date__confirm"
          :disabled="!pickedDate"
          @click="confirm"
        >
          Verder
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/nl';

let counter = 0;

export default {
  name: 'QuestionDate',

  components: {
    DatePicker,
  },

  props: {
    question: { type: Object, required: true },
    duration: { type: String, default: 'day' },
  },

  emits: ['answer'],

  data() {
    return {
      pickedDate: null,
      pickerId: 'sf-date-' + (++counter),
    };
  },

  computed: {
    placeholderText() {
      return 'Kies aankomstdatum';
    },

    calculatedRange() {
      if (!this.pickedDate) return null;

      const startDate = new Date(this.pickedDate);
      let endDate = new Date(this.pickedDate);

      const dur = (this.duration || '').toLowerCase();
      if (dur === 'weekend') {
        endDate.setDate(startDate.getDate() + 2);
      } else if (dur === 'midweek') {
        endDate.setDate(startDate.getDate() + 4);
      } else if (dur === 'week') {
        endDate.setDate(startDate.getDate() + 7);
      } else if (dur === 'longer') {
        endDate.setDate(startDate.getDate() + 14);
      } else {
        // 'day' of default: 1 dag
        endDate = new Date(startDate);
      }

      const label = this.formatRange(startDate, endDate, dur);
      return {
        startDate,
        endDate,
        label,
      };
    },
  },

  methods: {
    disabledDates(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date < today;
    },

    formatDutchDate(d, includeYear = true) {
      const months = [
        'januari', 'februari', 'maart', 'april', 'mei', 'juni',
        'juli', 'augustus', 'september', 'oktober', 'november', 'december'
      ];
      const day = d.getDate();
      const month = months[d.getMonth()];
      const year = d.getFullYear();
      if (includeYear) {
        return `${day} ${month} ${year}`;
      }
      return `${day} ${month}`;
    },

    formatRange(start, end, dur) {
      if (dur === 'day' || start.toDateString() === end.toDateString()) {
        return this.formatDutchDate(start, true);
      }

      const sameMonth = start.getMonth() === end.getMonth();
      const sameYear = start.getFullYear() === end.getFullYear();

      if (sameMonth && sameYear) {
        return `${start.getDate()} t/m ${this.formatDutchDate(end, true)}`;
      }

      if (sameYear) {
        return `${this.formatDutchDate(start, false)} t/m ${this.formatDutchDate(end, true)}`;
      }

      return `${this.formatDutchDate(start, true)} t/m ${this.formatDutchDate(end, true)}`;
    },

    toIso(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },

    confirm() {
      if (!this.calculatedRange) return;

      const { startDate, endDate, label } = this.calculatedRange;
      this.$emit('answer', {
        slot: this.question.slot || this.question.id,
        value: {
          start_date: this.toIso(startDate),
          end_date: this.toIso(endDate),
          formatted: label,
        },
        label: label,
      });
    },

    chooseForMe() {
      this.$emit('answer', {
        slot: this.question.slot || this.question.id,
        value: 'free_choice',
        label: 'Maakt niet uit',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.sf-question-date {
  padding: 8px 16px 16px;
  max-width: 440px;
  width: 100%;
  margin: 0 auto;

  &__box {
    background: #fff;
    border-radius: 20px;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  }

  &__picker-wrap {
    display: flex;
    flex-direction: column;
    gap: 6px;

    ::v-deep .mx-datepicker {
      width: 100%;
    }

    ::v-deep .mx-input {
      height: 46px;
      border-radius: 23px;
      font-family: 'Comfortaa', sans-serif;
      font-size: 15px;
      color: #23201e;
      border: 1.5px solid #b5afa5;
      padding: 0 16px;

      &:focus {
        border-color: #517b86;
        outline: none;
      }
    }
  }

  &__label {
    font-family: 'Comfortaa', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: #23201e;
    margin-bottom: 0;
  }

  &__preview {
    background: #f4ecdf;
    border-radius: 12px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'Comfortaa', sans-serif;
    font-size: 14px;
    color: #23201e;

    strong {
      color: #517b86;
    }
  }

  &__preview-icon {
    font-size: 16px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    margin-top: 4px;
  }

  &__btn-alt {
    background: transparent;
    border: 1.5px solid #23201e;
    color: #23201e;
    padding: 8px 18px;
    border-radius: 20px;
    font-family: 'Comfortaa', sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: #23201e;
      color: #fff;
    }
  }

  &__confirm {
    background: #23201e;
    color: #fff;
    border: none;
    border-radius: 24px;
    padding: 10px 24px;
    font-family: 'Comfortaa', sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s;

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }

    &:not(:disabled):hover {
      opacity: 0.85;
    }

    &:focus-visible {
      outline: 2px solid #517b86;
      outline-offset: 2px;
    }
  }
}
</style>
